import React, { FunctionComponent } from 'react'
import { Button } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import {
  useSaleManager,
  useTrainingModeManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectCurrentSale } from '../../../state/slices/currentSale/currentSaleSlice'
import { isQuote } from '../../../functions/sale'
import { SaleSyncStatus } from '../../../models/sale.constants'
import { selectIsFinalisingSale } from '../../../state/slices/uiStateSlice'
import { useIsOffline } from '../../shared/useIsOffline'
import { useCloseSaleConfirmation } from './useCloseSaleConfirmation'
import { useCompleteTutorial } from './useCompleteTutorial'
import { QuotePaymentRequestState } from './QuotePaymentRequestCard'

interface SaleActionBarProps {
  actionDisabled?: boolean
  isPickedUp: boolean
  setIsPickedUp: (value: boolean) => void
  quotePaymentRequest?: QuotePaymentRequestState
}

const COMPLETE_SALE_CONTEXT = 'Complete sale button'

export const SaleActionBar: FunctionComponent<SaleActionBarProps> = ({
  actionDisabled,
  isPickedUp,
  setIsPickedUp,
  quotePaymentRequest,
}) => {
  const trainingModeManager = useTrainingModeManager()
  const sale = useAppSelector(selectCurrentSale)
  const isFinalising = useAppSelector(selectIsFinalisingSale)
  const saleManager = useSaleManager()
  const closeSaleConfirmation = useCloseSaleConfirmation(
    isPickedUp,
    setIsPickedUp,
    quotePaymentRequest
  )
  const completeTutorial = useCompleteTutorial()
  const isOffline = useIsOffline()

  const disabled =
    (actionDisabled ||
      sale.syncStatus === SaleSyncStatus.UPLOAD_FIRST_SUBMIT ||
      isFinalising ||
      saleManager.shouldDisplayEmailErrorBanner()) &&
    // When offline, we don't want to check for these conditions,
    // Allow completing the sale.
    !isOffline

  const completeSaleText = isQuote(sale.status) ? (
    <T _str="Complete quote" _context={COMPLETE_SALE_CONTEXT} />
  ) : (
    <T _str="Complete sale" _context={COMPLETE_SALE_CONTEXT} />
  )

  return (
    <div className="sale-action-bar">
      {trainingModeManager.trainingModeOnboardingState ? (
        <Button
          onClick={completeTutorial}
          className="vd-btn vd-btn--go vd-btn--jumbo vd-flex--align-center vd-flex--justify-center btn--exit-mode sale-confirmation-form-row"
          data-track="complete-tutorial"
        >
          <T _str="Complete tutorial" _context={COMPLETE_SALE_CONTEXT} />
        </Button>
      ) : (
        <Button
          onClick={() => closeSaleConfirmation('button')}
          variant="go"
          jumbo
          disabled={disabled}
          data-cy="complete-action-button"
          data-testid="complete-action-button"
          className="btn-complete vd-flex--align-center vd-flex--justify-center sale-confirmation-form-row"
          data-track={
            trainingModeManager.trainingModeEnabled
              ? 'start-new-sale-in-training-mode'
              : 'complete-sale'
          }
        >
          {trainingModeManager.trainingModeEnabled ? (
            <T
              _str="Start a new sale in training mode"
              _context={COMPLETE_SALE_CONTEXT}
            />
          ) : (
            completeSaleText
          )}
        </Button>
      )}
    </div>
  )
}
